import type {
  FrameworkInfo,
  FrameworkSkillRubric,
  ScoringConfigResponse,
} from "@shared/labs/evaluation-settings-schemas";
import { validateFdeFloorsMarkdown } from "@shared/labs/fde-floors";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ChevronDown, ChevronUp, Loader2, Plus, Save, Trash2 } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { queryClient } from "@/lib/queryClient";
import { rpcClient } from "@/lib/rpc";
import { useMarkdownHtml } from "@/pages/labs/_shared/use-markdown-html";

export function FrameworksSection() {
  const { toast } = useToast();
  const [expandedSlug, setExpandedSlug] = useState<string | null>(null);
  const [addingNew, setAddingNew] = useState(false);
  const [newSlug, setNewSlug] = useState("");
  const [deleteTarget, setDeleteTarget] = useState<{ slug: string; name: string } | null>(null);
  const newSlugRef = useRef<HTMLInputElement>(null);

  const { data: frameworks, isLoading } = useQuery<FrameworkInfo[]>({
    // biome-ignore lint/security/noSecrets: query key, not a secret
    queryKey: ["labs", "evaluationSettingsListFrameworks"],
    queryFn: () => rpcClient.labs.evaluationSettingsListFrameworks(),
  });

  const handleAddNew = useCallback(() => {
    setAddingNew(true);
    setTimeout(() => newSlugRef.current?.focus(), 100);
  }, []);

  const handleCreateFramework = useCallback(async () => {
    const slug = newSlug
      .trim()
      .toLowerCase()
      .replace(/\s+/g, "-")
      .replace(/[^a-z0-9-]/g, "");
    if (!slug) {
      toast({ title: "Informe um identificador para a disciplina", variant: "destructive" });
      return;
    }

    const template = `## Disciplina: ${newSlug.trim()}

### Visão Geral
Descreva a visão geral desta disciplina...

### Skills

#### 01. Skill Name
Tagline: Uma frase curta.

##### L3 — Label (X/5)
Descrição do nível L3...
- Bullet point 1
- Bullet point 2

##### L4 — Label (X/5)
Descrição do nível L4...
- Bullet point 1

##### L5 — Label (X/5)
Descrição do nível L5...
- Bullet point 1`;

    try {
      // Newly created disciplines start as a monolith row; the split happens later
      // via the migration (server/scripts/split-skill-rubrics-*.ts) once the
      // CHRO has authored the per-skill bodies. Keeping creation simple avoids
      // requiring the operator to author 5 separate rubric rows up front.
      await rpcClient.labs.evaluationSettingsPut({ section: `framework:${slug}`, data: template });
      // biome-ignore lint/security/noSecrets: query key
      queryClient.invalidateQueries({ queryKey: ["labs", "evaluationSettingsListFrameworks"] });
      toast({ title: `Framework "${newSlug.trim()}" criado` });
      setAddingNew(false);
      setNewSlug("");
      setExpandedSlug(slug);
    } catch {
      toast({ title: "Erro ao criar framework", variant: "destructive" });
    }
  }, [newSlug, toast]);

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4 py-4">
      {/* List of existing frameworks */}
      {(!frameworks || frameworks.length === 0) && !addingNew && (
        <div className="text-center py-6 text-muted-foreground">
          <p>Nenhum framework de disciplina cadastrado.</p>
          <p className="text-sm mt-1">
            Adicione frameworks para avaliar candidatos contra skills específicas.
          </p>
        </div>
      )}

      {frameworks?.map((fw) => (
        <div key={fw.slug} className="border rounded-lg overflow-hidden">
          <button
            type="button"
            className="w-full flex items-center justify-between px-4 py-3 hover:bg-accent/50 transition-colors"
            onClick={() => setExpandedSlug(expandedSlug === fw.slug ? null : fw.slug)}
          >
            <div className="flex items-center gap-3">
              <span className="font-medium">{fw.name}</span>
              <div className="flex gap-1 pointer-events-none">
                {fw.levels.map((l) => (
                  <span
                    key={l}
                    className="inline-flex items-center rounded-full border bg-muted/40 px-2 py-0.5 text-[10px] font-medium text-muted-foreground"
                  >
                    {l}
                  </span>
                ))}
              </div>
            </div>
            {expandedSlug === fw.slug ? (
              <ChevronUp className="h-4 w-4 text-muted-foreground" />
            ) : (
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            )}
          </button>

          {expandedSlug === fw.slug && (
            <div className="border-t px-4 pb-4">
              <FrameworkEditor
                slug={fw.slug}
                sectionKey={fw.sectionKey}
                skillRubrics={fw.skillRubrics}
                onDelete={() => setDeleteTarget({ slug: fw.slug, name: fw.name })}
              />
            </div>
          )}
        </div>
      ))}

      {/* Add new framework */}
      {addingNew && (
        <div className="border rounded-lg p-4 space-y-3 border-dashed border-primary/30">
          <div className="text-sm font-medium">Nova Disciplina</div>
          <Input
            ref={newSlugRef}
            value={newSlug}
            onChange={(e) => setNewSlug(e.target.value)}
            placeholder="Nome da disciplina (ex: Engineering, Sales, etc.)"
            onKeyDown={(e) => {
              if (e.key === "Enter") handleCreateFramework();
            }}
          />
          <div className="flex gap-2">
            <Button size="sm" onClick={handleCreateFramework}>
              Criar
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setAddingNew(false);
                setNewSlug("");
              }}
            >
              Cancelar
            </Button>
          </div>
        </div>
      )}

      <Button
        variant="outline"
        className="w-full border-dashed"
        onClick={handleAddNew}
        disabled={addingNew}
      >
        <Plus className="h-4 w-4 mr-1" /> Adicionar Disciplina
      </Button>

      <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir disciplina?</AlertDialogTitle>
            <AlertDialogDescription>
              Isso excluirá permanentemente o framework "{deleteTarget?.name}" e todo o seu
              conteúdo. Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={async () => {
                if (!deleteTarget) return;
                try {
                  // Disciplines on the split layout live as `framework:<slug>:shell`,
                  // legacy ones as `framework:<slug>`. Try the split row first and
                  // fall through to the monolith on 404 so deleting a discipline
                  // works regardless of which shape is currently in the DB.
                  const shellKey = `framework:${deleteTarget.slug}:shell`;
                  const monolithKey = `framework:${deleteTarget.slug}`;
                  let deleted = false;
                  try {
                    await rpcClient.labs.evaluationSettingsDelete({ section: shellKey });
                    deleted = true;
                  } catch {
                    /* fall through */
                  }
                  if (!deleted) {
                    await rpcClient.labs.evaluationSettingsDelete({ section: monolithKey });
                  }
                  queryClient.invalidateQueries({
                    // biome-ignore lint/security/noSecrets: query key
                    queryKey: ["labs", "evaluationSettingsListFrameworks"],
                  });
                  toast({ title: `Framework "${deleteTarget.name}" excluído` });
                  setExpandedSlug(null);
                } catch {
                  toast({ title: "Erro ao excluir framework", variant: "destructive" });
                }
                setDeleteTarget(null);
              }}
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/** Editor for a single framework. Renders the shell (intro + level meta + floor
 *  table) at the top and one collapsible editor per per-skill rubric below.
 *  After the 2026-05-01 split the operator needs both — the shell carries the
 *  hire-gate floor table, and each rubric carries its own outcome-oriented
 *  scoring guidance. Without the rubric editors the CHRO has no UI to tune the
 *  scoring rules; only SQL would work. */
function FrameworkEditor({
  slug,
  sectionKey,
  skillRubrics,
  onDelete,
}: {
  slug: string;
  sectionKey: string;
  skillRubrics: FrameworkSkillRubric[];
  onDelete: () => void;
}) {
  const { toast } = useToast();
  const [content, setContent] = useState("");
  const [initialized, setInitialized] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  const queryKey = ["labs", "evaluationSettingsGet", sectionKey];

  const { data, isLoading } = useQuery<ScoringConfigResponse>({
    queryKey,
    queryFn: () => rpcClient.labs.evaluationSettingsGet({ section: sectionKey }),
  });

  useEffect(() => {
    if (data && !initialized) {
      setContent(String(data.data ?? ""));
      setInitialized(true);
    }
  }, [data, initialized]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      await rpcClient.labs.evaluationSettingsPut({ section: sectionKey, data: content });
    },
    onSuccess: () => {
      setIsDirty(false);
      queryClient.invalidateQueries({ queryKey });
      // biome-ignore lint/security/noSecrets: query key
      queryClient.invalidateQueries({ queryKey: ["labs", "evaluationSettingsListFrameworks"] });
      toast({ title: "Framework salvo com sucesso" });
    },
    onError: (err: Error) => {
      toast({ title: "Erro ao salvar", description: err.message, variant: "destructive" });
    },
  });

  // FDE-only: the floor table inside the markdown drives the runtime hire-gate
  // (see shared/labs/fde-floors.ts). A typo or missing row would make the
  // server fall back to the canonical constants, silently — better to block
  // the save and tell the CHRO exactly what's wrong. Other frameworks have no
  // floor table, so validation is skipped entirely.
  const floorErrors = slug === "forward-deployed" ? validateFdeFloorsMarkdown(content) : [];

  const handleSave = () => {
    if (floorErrors.length > 0) {
      toast({
        title: "Tabela de mínimos inválida",
        description: floorErrors.slice(0, 3).join(" "),
        variant: "destructive",
      });
      return;
    }
    saveMutation.mutate();
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-4">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-3 pt-3">
      <FrameworkEditorToggle
        content={content}
        onChange={(next) => {
          setContent(next);
          setIsDirty(true);
        }}
      />
      {data?.updatedAt && (
        <p className="text-xs text-muted-foreground">
          Última alteração: {new Date(data.updatedAt).toLocaleString("pt-BR")}
          {isDirty && (
            <span className="ml-2 font-medium text-amber-700" data-testid="fde-dirty-pip">
              · edição não salva
            </span>
          )}
        </p>
      )}
      {floorErrors.length > 0 && (
        <div
          className="rounded border border-destructive/40 bg-destructive/5 px-3 py-2 text-xs text-destructive"
          data-testid="fde-floors-errors"
        >
          <p className="font-medium">Tabela de mínimos não passa na validação:</p>
          <ul className="mt-1 list-disc pl-4 space-y-0.5">
            {floorErrors.map((err) => (
              <li key={err}>{err}</li>
            ))}
          </ul>
        </div>
      )}
      <div className="flex justify-between">
        <Button
          variant="ghost"
          size="sm"
          className="text-destructive hover:text-destructive hover:bg-destructive/10"
          onClick={onDelete}
        >
          <Trash2 className="h-4 w-4 mr-1" />
          Excluir disciplina
        </Button>
        <Button
          onClick={handleSave}
          disabled={!isDirty || saveMutation.isPending || floorErrors.length > 0}
        >
          {saveMutation.isPending ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Save className="h-4 w-4 mr-2" />
          )}
          Salvar
        </Button>
      </div>

      {skillRubrics.length > 0 && (
        <div className="space-y-2 pt-2 border-t mt-3">
          <h4 className="text-sm font-medium pt-2">Rubricas por skill</h4>
          <p className="text-xs text-muted-foreground">
            Cada bloco abaixo é a rubrica oficial para uma skill desta disciplina. O agente de
            calibração lê estes textos diretamente — edite com cuidado.
          </p>
          {skillRubrics.map((rubric) => (
            <SkillRubricEditor key={rubric.sectionKey} rubric={rubric} />
          ))}
        </div>
      )}
    </div>
  );
}

/** Collapsible editor for one per-skill rubric row. The rubric body is plain
 *  markdown — same shape as the shell — so this just reuses the shell's editor
 *  toggle. Saves go straight to the matching `scoring:skill:rubric:*` row. */
function SkillRubricEditor({ rubric }: { rubric: FrameworkSkillRubric }) {
  const { toast } = useToast();
  const [expanded, setExpanded] = useState(false);
  const [content, setContent] = useState("");
  const [initialized, setInitialized] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  const queryKey = ["labs", "evaluationSettingsGet", rubric.sectionKey];

  const { data, isLoading } = useQuery<ScoringConfigResponse>({
    queryKey,
    queryFn: () => rpcClient.labs.evaluationSettingsGet({ section: rubric.sectionKey }),
    enabled: expanded,
  });

  useEffect(() => {
    if (data && !initialized) {
      setContent(String(data.data ?? ""));
      setInitialized(true);
    }
  }, [data, initialized]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      await rpcClient.labs.evaluationSettingsPut({ section: rubric.sectionKey, data: content });
    },
    onSuccess: () => {
      setIsDirty(false);
      queryClient.invalidateQueries({ queryKey });
      // biome-ignore lint/security/noSecrets: query key
      queryClient.invalidateQueries({ queryKey: ["labs", "evaluationSettingsListFrameworks"] });
      toast({ title: `Rubrica "${rubric.label}" salva` });
    },
    onError: (err: Error) => {
      toast({ title: "Erro ao salvar", description: err.message, variant: "destructive" });
    },
  });

  return (
    <div className="border rounded-md overflow-hidden">
      <button
        type="button"
        className="w-full flex items-center justify-between px-3 py-2 hover:bg-accent/50 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <span className="text-sm font-medium">{rubric.label}</span>
        {expanded ? (
          <ChevronUp className="h-4 w-4 text-muted-foreground" />
        ) : (
          <ChevronDown className="h-4 w-4 text-muted-foreground" />
        )}
      </button>
      {expanded && (
        <div className="border-t px-3 pb-3 pt-2 space-y-2">
          {isLoading ? (
            <div className="flex justify-center py-4">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <>
              <FrameworkEditorToggle
                content={content}
                onChange={(next) => {
                  setContent(next);
                  setIsDirty(true);
                }}
              />
              {data?.updatedAt && (
                <p className="text-xs text-muted-foreground">
                  Última alteração: {new Date(data.updatedAt).toLocaleString("pt-BR")}
                  {isDirty && (
                    <span className="ml-2 font-medium text-amber-700">· edição não salva</span>
                  )}
                </p>
              )}
              <div className="flex justify-end">
                <Button
                  size="sm"
                  onClick={() => saveMutation.mutate()}
                  disabled={!isDirty || saveMutation.isPending}
                >
                  {saveMutation.isPending ? (
                    <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />
                  ) : (
                    <Save className="h-3.5 w-3.5 mr-2" />
                  )}
                  Salvar rubrica
                </Button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Editor + preview toggle for the discipline markdown body. The body lives
 * as raw markdown in the DB; the operator edits it as plain text and
 * occasionally wants to see how it renders before saving. This toggle is
 * the cheapest viable affordance: a two-tab switch between the textarea
 * and a styled markdown preview. Not a structured editor (deferred), but
 * a real readability win over a 13 KB monospace blob.
 */
function FrameworkEditorToggle({
  content,
  onChange,
}: {
  content: string;
  onChange: (next: string) => void;
}) {
  const [view, setView] = useState<"edit" | "preview">("edit");
  return (
    <div className="space-y-2">
      <div
        role="tablist"
        aria-label="Modo do editor de disciplina"
        className="inline-flex items-center gap-1 rounded-md border border-border bg-muted/40 p-0.5"
      >
        <FrameworkViewTab active={view === "edit"} onClick={() => setView("edit")}>
          Editar
        </FrameworkViewTab>
        <FrameworkViewTab active={view === "preview"} onClick={() => setView("preview")}>
          Visualizar
        </FrameworkViewTab>
      </div>
      {view === "edit" ? (
        <Textarea
          value={content}
          onChange={(e) => onChange(e.target.value)}
          rows={20}
          className="font-mono text-sm"
          placeholder="Markdown do framework de skills..."
        />
      ) : (
        <FrameworkPreview content={content} />
      )}
    </div>
  );
}

function FrameworkViewTab({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={
        active
          ? "rounded px-3 py-1 text-xs font-semibold bg-background shadow-sm text-foreground"
          : "rounded px-3 py-1 text-xs font-medium text-muted-foreground hover:text-foreground"
      }
    >
      {children}
    </button>
  );
}

function FrameworkPreview({ content }: { content: string }) {
  // Lazy-loaded so the marked dependency only ships when the operator
  // actually flips into preview mode — keeps the default editor bundle
  // lean.
  const html = useMarkdownHtml(content);
  return (
    <article
      className="framework-preview prose prose-sm max-w-none rounded-md border border-border bg-background p-4"
      // biome-ignore lint/security/noDangerouslySetInnerHtml: trusted internal markdown body
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

// `useMarkdownHtml` moved to `@/pages/labs/_shared/use-markdown-html` so
// the same lazy-loaded markdown renderer powers both this preview and the
// candidate-intelligence market-level reasoning + the archived-evaluation
// detail overlay reasoning. See that module for the canonical impl.
